// Admin — Announcement Ticker manager.
// CRUD on messages, reorder, toggle on/off, speed + color settings, live preview.
import { useCallback, useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Save, Pause, Play } from "lucide-react";
import { API, RED, NEON, AMBER, authHeader } from "./shared";

export function TickerManager({ token }) {
  const [settings, setSettings] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [adding, setAdding] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/ticker/admin`, { headers: authHeader(token) })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) { setSettings(d.settings); setMsgs(d.messages); }
      });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ---- messages ----
  async function add() {
    if (!adding.trim()) return;
    await fetch(`${API}/ticker/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ text: adding.trim() }),
    });
    setAdding(""); load();
  }
  async function patch(id, body) {
    await fetch(`${API}/ticker/messages/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify(body),
    });
    load();
  }
  async function remove(id) {
    await fetch(`${API}/ticker/messages/${id}`, { method: "DELETE", headers: authHeader(token) });
    load();
  }
  async function move(idx, delta) {
    const next = idx + delta;
    if (next < 0 || next >= msgs.length) return;
    const arr = [...msgs];
    const [m] = arr.splice(idx, 1);
    arr.splice(next, 0, m);
    setMsgs(arr);
    await fetch(`${API}/ticker/messages/reorder`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ order: arr.map((m) => m.id) }),
    });
  }

  // ---- settings ----
  function setS(k, v) { setSettings((s) => ({ ...s, [k]: v })); setDirty(true); }
  async function saveSettings() {
    await fetch(`${API}/ticker/settings`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify(settings),
    });
    setDirty(false); load();
  }

  if (!settings) return <p className="px-5 py-10 text-center text-white/45">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="ticker-manager">
      {/* Live preview */}
      <div className="rounded-sm border border-white/10 bg-black/50">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2">
            <Megaphone size={14} color={RED} /> ANNOUNCEMENT TICKER
          </p>
          <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">LIVE PREVIEW</span>
        </div>
        <TickerPreview settings={settings} msgs={msgs} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Settings panel */}
        <section className="rounded-sm border border-white/10 bg-black/50 p-5 space-y-4" data-testid="ticker-settings">
          <p className="font-heavy text-[12px] tracking-[0.14em] uppercase text-white/65">Settings</p>
          {/* On/Off */}
          <button onClick={() => setS("enabled", !settings.enabled)}
            data-testid="ticker-toggle-enabled"
            className="w-full flex items-center justify-between px-3 py-2 rounded-sm border border-white/15 text-[12px] text-white">
            <span className="font-extrabold tracking-[0.10em] uppercase">Show On Site</span>
            {settings.enabled ? <ToggleRight size={20} color={NEON} /> : <ToggleLeft size={20} color="rgba(255,255,255,0.35)" />}
          </button>
          {/* Speed */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] tracking-[0.16em] font-bold text-white/55">SPEED</span>
              <span className="text-[10.5px] font-mono text-white/70">{settings.speed_sec}s / cycle</span>
            </div>
            <input type="range" min={10} max={120} step={2} value={settings.speed_sec}
              onChange={(e) => setS("speed_sec", parseInt(e.target.value, 10))}
              data-testid="ticker-speed"
              className="w-full accent-[#FF2A3C]" />
            <div className="flex justify-between text-[9px] tracking-[0.18em] font-bold text-white/35 mt-1">
              <span><Play size={9} className="inline" /> FAST</span>
              <span>SLOW <Pause size={9} className="inline" /></span>
            </div>
          </div>
          {/* Colors */}
          <ColorRow label="BACKGROUND" value={settings.background} onChange={(v) => setS("background", v)} testid="ticker-bg" />
          <ColorRow label="TEXT"       value={settings.foreground} onChange={(v) => setS("foreground", v)} testid="ticker-fg" />
          <ColorRow label="ACCENT DOT" value={settings.accent}     onChange={(v) => setS("accent", v)}     testid="ticker-accent" />
          {/* Pause */}
          <label className="flex items-center justify-between px-3 py-2 rounded-sm border border-white/15 cursor-pointer">
            <span className="text-[12px] font-extrabold tracking-[0.10em] uppercase text-white">Pause On Hover</span>
            <input type="checkbox" checked={settings.pause_on_hover}
              onChange={(e) => setS("pause_on_hover", e.target.checked)}
              data-testid="ticker-pauseonhover"
              className="accent-[#FF2A3C] w-4 h-4" />
          </label>
          <button onClick={saveSettings} disabled={!dirty}
            data-testid="ticker-save"
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-extrabold tracking-[0.14em] uppercase border"
            style={{ background: dirty ? RED : "transparent", borderColor: RED, color: dirty ? "white" : RED }}>
            <Save size={11} /> {dirty ? "Save Settings" : "Saved"}
          </button>
        </section>

        {/* Messages list */}
        <section className="lg:col-span-2 rounded-sm border border-white/10 bg-black/50" data-testid="ticker-messages">
          <header className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <p className="font-heavy text-[12px] tracking-[0.14em] uppercase text-white/65">Messages — {msgs.length}</p>
            <span className="text-[9.5px] tracking-[0.16em] font-bold text-white/45">REORDER · TOGGLE · DELETE</span>
          </header>
          {/* Add row */}
          <div className="px-5 py-3 border-b border-white/5 flex gap-2">
            <input value={adding} onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="NEW ANNOUNCEMENT (e.g. FREE SHIPPING ON ALL ORDERS)"
              data-testid="ticker-add-input"
              className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none" />
            <button onClick={add} disabled={!adding.trim()}
              data-testid="ticker-add-btn"
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
              style={{ background: RED, borderColor: RED, color: "white" }}>
              <Plus size={11} /> Add
            </button>
          </div>
          <div className="divide-y divide-white/5 max-h-[520px] overflow-y-auto">
            {msgs.length === 0 && <p className="text-center text-white/45 text-[12px] py-10">No messages yet.</p>}
            {msgs.map((m, idx) => (
              <MessageRow key={m.id} m={m} idx={idx} total={msgs.length}
                onMove={move} onPatch={patch} onRemove={remove} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function MessageRow({ m, idx, total, onMove, onPatch, onRemove }) {
  const [text, setText] = useState(m.text);
  const [editing, setEditing] = useState(false);
  return (
    <div className="grid grid-cols-12 gap-2 px-3 sm:px-5 py-2.5 items-center" data-testid={`ticker-row-${m.id.slice(0,8)}`}>
      <div className="col-span-1 flex flex-col gap-0.5">
        <button onClick={() => onMove(idx, -1)} disabled={idx === 0}
          data-testid={`ticker-up-${m.id.slice(0,8)}`}
          className="text-white/55 disabled:opacity-25 hover:text-white"><ChevronUp size={14} /></button>
        <button onClick={() => onMove(idx, +1)} disabled={idx === total - 1}
          data-testid={`ticker-down-${m.id.slice(0,8)}`}
          className="text-white/55 disabled:opacity-25 hover:text-white"><ChevronDown size={14} /></button>
      </div>
      <div className="col-span-7 sm:col-span-8">
        {editing ? (
          <input value={text} onChange={(e) => setText(e.target.value)}
            onBlur={() => { setEditing(false); if (text.trim() && text !== m.text) onPatch(m.id, { text: text.trim() }); }}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            autoFocus
            data-testid={`ticker-edit-${m.id.slice(0,8)}`}
            className="w-full bg-black border border-white/15 px-2 py-1.5 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none" />
        ) : (
          <button onClick={() => setEditing(true)}
            data-testid={`ticker-text-${m.id.slice(0,8)}`}
            className="w-full text-left text-[12.5px] tracking-[0.10em] font-extrabold uppercase truncate"
            style={{ color: m.color || "white" }}>
            {m.text}
          </button>
        )}
      </div>
      <div className="col-span-1 flex justify-center">
        <input type="color" value={m.color || "#FFFFFF"}
          onChange={(e) => onPatch(m.id, { color: e.target.value })}
          title="Highlight color (defaults to white)"
          data-testid={`ticker-color-${m.id.slice(0,8)}`}
          className="w-7 h-7 bg-transparent border border-white/15 rounded-sm cursor-pointer p-0.5" />
      </div>
      <div className="col-span-2 sm:col-span-1 flex justify-end">
        <button onClick={() => onPatch(m.id, { enabled: !m.enabled })}
          data-testid={`ticker-enabled-${m.id.slice(0,8)}`}
          title={m.enabled ? "Disable" : "Enable"}>
          {m.enabled ? <ToggleRight size={20} color={NEON} /> : <ToggleLeft size={20} color="rgba(255,255,255,0.35)" />}
        </button>
      </div>
      <div className="col-span-1 flex justify-end">
        <button onClick={() => onRemove(m.id)} data-testid={`ticker-delete-${m.id.slice(0,8)}`}
          className="text-white/45 hover:text-[#FF2A3C]">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div>
      <span className="text-[10px] tracking-[0.16em] font-bold text-white/55 block mb-1">{label}</span>
      <div className="flex gap-2 items-center">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-color`}
          className="w-10 h-9 bg-transparent border border-white/15 rounded-sm cursor-pointer p-0.5" />
        <input value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-hex`}
          className="flex-1 bg-black border border-white/15 px-2 py-1.5 text-white font-mono text-[11.5px] focus:border-[#FF2A3C] focus:outline-none" />
      </div>
    </div>
  );
}

/* Live preview — renders inside the admin tab so the owner sees changes instantly. */
function TickerPreview({ settings, msgs }) {
  if (!settings.enabled || msgs.filter((m) => m.enabled).length === 0) {
    return <div className="px-5 py-6 text-center text-white/45 text-[12px]" data-testid="ticker-preview-empty">
      Ticker disabled OR no enabled messages — nothing renders on the public site.
    </div>;
  }
  const enabled = msgs.filter((m) => m.enabled);
  const doubled = [...enabled, ...enabled];
  return (
    <div className="relative overflow-hidden" data-testid="ticker-preview"
      style={{ background: settings.background, color: settings.foreground }}>
      <style>{`
        @keyframes admin-ticker { 0% { transform: translate3d(0,0,0); } 100% { transform: translate3d(-50%,0,0); } }
        .admin-ticker-track { display: inline-flex; will-change: transform;
          animation: admin-ticker ${settings.speed_sec}s linear infinite; }
      `}</style>
      <div className="py-3 whitespace-nowrap">
        <div className="admin-ticker-track">
          {doubled.map((m, i) => (
            <span key={`${m.id}-${i}`}
              className="inline-flex items-center px-5 text-[12px] tracking-[0.22em] font-extrabold uppercase"
              style={m.color ? { color: m.color } : undefined}>
              <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full mr-4" style={{ background: m.color || settings.accent }} />
              {m.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
